import { useMutation } from '@apollo/client';
import MaterialButton from '@mui/material/Button';
import Grid from '@mui/material/Grid';
import Tab from '@mui/material/Tab';
import Tabs from '@mui/material/Tabs';
import * as Sentry from '@sentry/react';
import arrayMutators from 'final-form-arrays';
import { MouseEvent } from 'react';
import React, { FC, PureComponent, useCallback, useEffect, useRef, useState } from 'react';
import { Form } from 'react-final-form';
import { Helmet } from 'react-helmet';
import { ConnectedProps, connect } from 'react-redux';
import { useNavigation, useRevalidator } from 'react-router';
import { Waypoint } from 'react-waypoint';

import { gengql, useFragment } from '@/__generated__';
import {
  ExpertRequestTransitionAction,
  ExpertRequestType,
  IsGoodMatch,
  ProjectMemberState,
  ProjectRole,
} from '@/__generated__/graphql';
import { setAddress } from '@/actions/address';
import { updateProjectMember } from '@/actions/project';
import { notify } from '@/actions/ui';
import Button from '@/components/Button';
import CircularProgress from '@/components/CircularProgress';
import ConsultationList from '@/components/ConsultationList';
import ConsultationsStarting from '@/components/ConsultationsStarting';
import CountBox from '@/components/CountBox';
import EditIcon from '@/components/EditIcon';
import EmptyMessage from '@/components/EmptyMessage';
import FAIcon from '@/components/Icon/FAIcon';
import LayoutPage from '@/components/Layout/LayoutPage';
import Link from '@/components/Link';
import MediaQuery from '@/components/MediaQuery';
import MemberRequests from '@/components/MemberRequests/Project';
import { SendMessageDialog } from '@/components/MessageTemplate';
import ProjectBar from '@/components/ProjectBar';
import ProjectBarFallback from '@/components/ProjectBar/ProjectBarFallback';
import ShareableLink from '@/components/ShareableLink';
import { ERROR_CODES, apiErrorMessage, hasErrorCode } from '@/core/api';
import { sortMembers } from '@/core/project';
import { ExpertRequest } from '@/expertrequest';
import { buildRefUrl } from '@/expertrequest';
import { ActivityFeed } from '@/expertrequest/components/ActivityFeed';
import CandidateWidgetHorizontal from '@/expertrequest/components/CandidateWidgetHorizontal';
import EditExpertRequest from '@/expertrequest/components/EditExpertRequest';
import { ErStageBanner } from '@/expertrequest/components/ErStageBanner';
import { ErStageDropdown } from '@/expertrequest/components/ErStageDropdown';
import { ErTransitionModals } from '@/expertrequest/components/ErTransitionModals';
import Details from '@/expertrequest/components/ExpertRequestForm/DetailsNew';
import { InternalErFields } from '@/expertrequest/components/ExpertRequestForm/InternalErFields';
import Name from '@/expertrequest/components/ExpertRequestForm/Name';
import SubmitContainer from '@/expertrequest/components/ExpertRequestForm/SubmitContainer';
import ReferFriend from '@/expertrequest/components/ReferFriend';
import { useErTransitions } from '@/expertrequest/hooks/useErTransitions';
import { useReportMissingProject } from '@/expertrequest/reportMissingProject';
import {
  isDeprecatedType,
  removeExpertRequestCandidate,
  saveExpertRequest,
} from '@/expertrequest/store';
import { PROJECT_FIELDS } from '@/expertrequest/store/queries';
import { countOpenConsultations } from '@/expertrequest/utils/consultationGate';
import { canEditGroup } from '@/expertrequest/utils/fieldPermissions';
import { isEngagementsEnabledForGroup } from '@/features/consulting-engagements/gate';
import { useApp } from '@/hooks/useAppContext';
import SelectedProfilesBar from '@/search/components/SelectedProfilesBar';
import { RootState } from '@/store';
import { surfaceCard } from '@/theme/colors';
import { SCREEN_SM } from '@/theme/screens';
import { add, removeAt } from '@/utils/reducer';

import s from './ExpertRequestPrivate.module.scss';
import { selectExpertRequestProps } from './liveExpertRequest';

const ADD_EXPERT_REQUEST_ATTACHMENTS = gengql(/* GraphQL */ `
  mutation requestAddAttachmentsUpdateER(
    $expertRequestId: String!
    $attachments: [EngagementAttachmentInput]!
  ) {
    requestAddAttachments(expert_request_id: $expertRequestId, attachments: $attachments) {
      id
      created_at
      author {
        id
        name
        html_url
      }
      expert_request_id
      consultation_id
      name
      description
      file_url
      hide_from_experts
    }
  }
`);

const REMOVE_EXPERT_REQUEST_ATTACHMENTS = gengql(/* GraphQL */ `
  mutation requestRemoveAttachmentsUpdateER($expertRequestId: String!, $attachmentIds: [String!]!) {
    requestRemoveAttachments(expert_request_id: $expertRequestId, attachment_ids: $attachmentIds) {
      id
      created_at
      author {
        id
        name
        html_url
      }
      expert_request_id
      consultation_id
      name
      description
      file_url
      hide_from_experts
    }
  }
`);

const dialogs: Record<string, any> = {
  name: {
    component: Name,
    title: 'Edit Title',
  },
};

function sortDates(a: string, b: string) {
  return new Date(a).getTime() - new Date(b).getTime();
}

interface PromoProps {
  title: string;
  children: React.ReactNode;
}

class Promo extends PureComponent<PromoProps> {
  render() {
    const { title, children } = this.props;
    return (
      <div className={s.promo}>
        <div>
          <h3 className={s.promoTitle}>{title}</h3>
        </div>
        <div>{children}</div>
      </div>
    );
  }
}

const DETAILS_SHOW_FIELDS = [
  'er_type',
  'description',
  'qualifications',
  'questions',
  'companies',
  'disclosure',
  'sectors',
  'regions',
  'focusAreas',
  'group_about',
  'instructions_research',
  'job_scope',
  'opportunity_location',
  'attachments',
  'tags',
  'expected_duration',
  'time_done_scoping_call',
];

const titles = [
  'May the force be with you on this match.',
  'Good luck and godspeed, soldier.',
  'I feel the need... the need for speed.',
  'If you match them, they will come.',
  "Toto, I've a feeling we're not in Kansas anymore.",
  'Keep your friends close, but your experts closer.',
  'Go ahead, make my match.',
  'We love the smell of expert matches in the morning.',
  'Happy matching! And may the odds be ever in your favor.',
  "Life is like a box of matches. You never know who you're gonna get.",
  'To match, or not to match, that is the question.',
  'Match or match not. There is no try.',
  'Keep calm and match on.',
  "I'll be back... with a match.",
  "I'll make the expert an offer he can't refuse.",
  'Keep your friends close, but your experts closer.',
  'Not all those who wander are lost... some are just seeking the right expert.',
  'Matchmaker, matchmaker, make me a match, find me an expert, catch me a catch.',
];

interface RMStartPromoProps {
  expertRequest: ExpertRequest;
}

const rmStartPromoConnector = connect((state: RootState) => ({
  viewer: state.viewer,
}));

const RMStartPromoBare = ({
  viewer,
  expertRequest,
}: RMStartPromoProps & ConnectedProps<typeof rmStartPromoConnector>) => {
  const url = buildRefUrl(expertRequest, 'linkedin', viewer.id);
  const isAdmin = viewer.admin;

  const title = titles[parseInt(expertRequest.id) % titles.length];
  return (
    <Promo title={title}>
      <div style={{ marginBottom: 10 }}>
        <a href="https://www.notion.so/Fulfillment-Team-Wiki-d09340631e5c42f98e086dba51a213c9">
          Fulfillment Team Wiki
        </a>
      </div>
      <div style={{ marginBottom: 24 }}>
        <ShareableLink label="LinkedIn referral URL" url={url} enableShorten={isAdmin} />
      </div>
      <Button
        href="https://www.linkedin.com/search/results/people/"
        startIcon="binoculars"
        color="lightTan"
        size="medium"
        style={{ color: 'var(--onSurface)' }}
      >
        Start Searching
      </Button>
    </Promo>
  );
};

const RMStartPromo = rmStartPromoConnector(RMStartPromoBare);

interface CountBoxesState {
  selected: {
    suggestions: { [key: string]: boolean };
    experts: { [key: string]: boolean };
  };
}

interface CountBoxesProps {
  section: string;
  expertCounts: any;
  isClient: boolean;
  selectedBoxes: any;
  onClick: (states: any, boxes: any) => void;
}

class CountBoxes extends PureComponent<CountBoxesProps, CountBoxesState> {
  static boxes = {
    suggestions: [
      {
        id: 'suggestions-suggested',
        label: 'Suggested',
        states: ['suggested_by_platform', 'suggested_by_research'],
        hiddenFromClients: true,
      },
      {
        id: 'suggestions-contacted',
        label: 'Contacted',
        states: ['contacted'],
        hiddenFromClients: true,
      },
      {
        id: 'suggestions-interested',
        label: 'Interested',
        states: ['interested'],
        hiddenFromClients: true,
      },
      {
        id: 'suggestions-rejected',
        label: 'Rejected',
        states: ['rejected_suggestion'],
        hiddenFromClients: true,
        warn: true,
      },
    ],
    experts: [
      {
        id: 'experts-polishing',
        label: 'Polishing',
        states: ['polishing'],
        hiddenFromClients: true,
      },
      {
        id: 'experts-vetting',
        label: 'Vetting',
        states: ['vetting'],
      },
      {
        id: 'experts-verified',
        label: 'Verified',
        states: ['verified'],
      },
      {
        id: 'experts-matched',
        label: 'Matched',
        states: ['matched'],
      },
      {
        id: 'experts-rejected',
        label: 'Rejected',
        states: ['rejected_by_client', 'rejected_by_research'],
        warn: true,
      },
    ],
  };

  constructor(props: CountBoxesProps) {
    super(props);
    this.state = {
      selected: props.selectedBoxes || { suggestions: {}, experts: {} },
    };
  }

  postClick = () => {
    const { onClick } = this.props;
    const { selected } = this.state;

    const selectedStatesFor = (section: keyof CountBoxesState['selected']) =>
      CountBoxes.boxes[section as keyof typeof CountBoxes.boxes].reduce((acc: any, box: any) => {
        if (!selected[section as keyof CountBoxesState['selected']][box.id]) return acc;
        return [...acc, ...box.states];
      }, []);

    onClick(
      {
        suggestions: selectedStatesFor('suggestions'),
        experts: selectedStatesFor('experts'),
      },
      selected
    );
  };

  handleClick = (event: MouseEvent<HTMLAnchorElement | HTMLButtonElement>, box: any) => {
    event.preventDefault();
    const { section } = this.props as { section: 'suggestions' | 'experts' };

    this.setState(
      (prevState) => ({
        selected: {
          ...prevState.selected,
          [section]: {
            ...prevState.selected[section],
            [box.id]: !prevState.selected[section][box.id],
          },
        },
      }),
      () => this.postClick()
    );
  };

  sumCounts = (expertCounts: any, states: any) => {
    const matchingCounts = (states as string[])
      .filter((s) => Object.prototype.hasOwnProperty.call(expertCounts, s))
      .map((s) => expertCounts[s] as number);
    return matchingCounts.length > 0 ? matchingCounts.reduce((a, b) => a + b, 0) : 0;
  };

  render() {
    const { section, expertCounts, isClient } = this.props;
    const { selected } = this.state;
    const boxes = CountBoxes.boxes[section as keyof typeof CountBoxes.boxes].filter(
      (x) => !(isClient && x.hiddenFromClients)
    );

    if (!expertCounts) {
      return null;
    }

    return (
      <div className={s.countBoxes}>
        {boxes.map((b) => (
          <CountBox
            className={s.countBox}
            grayOutZero
            key={b.id}
            selected={selected[section as 'suggestions' | 'experts'][b.id]}
            label={b.label}
            count={this.sumCounts(expertCounts, b.states)}
            warn={b.warn}
            labelClassName={s.countBoxLabel}
            onClick={(event: MouseEvent<HTMLAnchorElement | HTMLButtonElement>) =>
              this.handleClick(event, b)
            }
          />
        ))}
      </div>
    );
  }
}

const PHONE_SAVE_ERROR = 'Error saving phone number.';

// TODO: replace with useQuery
const connector = connect(selectExpertRequestProps, {
  removeExpertRequestCandidate,
  saveExpertRequest,
  notify,
  updateProjectMember,
  setAddress,
});

export type SectionType = 'suggestions' | 'experts' | 'details' | 'consultations' | 'activity';

interface ExpertRequestProps {
  expertRequestId: string;
  candidateId?: string;
  isGoodMatch?: IsGoodMatch;
  fetchMoreCandidates: (
    type: 'matched' | 'suggested',
    pageInfo: Record<string, any>
  ) => Promise<void>;
  section: SectionType;
}

const ExpertRequestPrivate: FC<ExpertRequestProps & ConnectedProps<typeof connector>> = ({
  expertRequest,
  candidateId,
  section,
  notify,
  saveExpertRequest,
  setAddress,
  removeExpertRequestCandidate,
  isGoodMatch = IsGoodMatch.Unknown,
  fetchMoreCandidates,
  viewer,
  suggestedCandidates,
  matchedCandidates,
  loadingCandidates,
  loadedCandidates,
  suggestedCandidatesPageInfo,
  matchedCandidatesPageInfo,
}) => {
  const { store } = useApp();

  const [addAttachments] = useMutation(ADD_EXPERT_REQUEST_ATTACHMENTS);
  const [removeAttachments] = useMutation(REMOVE_EXPERT_REQUEST_ATTACHMENTS);

  const transitions = useErTransitions(expertRequest?.id ?? '');

  const [editDialog, setEditDialog] = useState<string | null>(null);
  const [stateSection, setSection] = useState<SectionType>(section || 'experts');
  const [selectedStates, setSelectedStates] = useState({
    suggestions: [],
    experts: [],
  });
  const [selectedBoxes, setSelectedBoxes] = useState(null);
  const [selectedProfiles, setSelectedProfiles] = useState<any[]>([]);
  const [sendMessageDialogOpen, setSendMessageDialogOpen] = useState(false);
  const candidateRefs = useRef(() => {
    const cands = (matchedCandidates || []).concat(suggestedCandidates || []);
    const refs: { [key: string]: React.RefObject<HTMLDivElement> } = {};
    cands.forEach((c) => (refs[c.id] = React.createRef()));
    return refs;
  });

  useEffect(() => {
    // @ts-expect-error error TS(2531) FIXME: Object is possibly 'null'.
    const selectedCandRef = candidateId && candidateRefs.current[candidateId]?.current;

    if (selectedCandRef) {
      const rect = selectedCandRef.getBoundingClientRect();
      const headerOffset = -45;
      const elementPosition = rect.top;
      const offsetPosition = elementPosition + headerOffset;
      window.scrollTo({ top: offsetPosition, behavior: 'smooth' });
    }

    async function fetchData() {
      // @ts-ignore FIXME: Object is possibly 'null'.
      await Promise.all(Details.fetch.map((f: any) => store.dispatch(f({ viewer }))));
    }

    fetchData();
  }, [candidateId, store, viewer]);

  const openDialog = useCallback(
    async (editDialog: string) => {
      const { component } = dialogs[editDialog];

      if (component.fetch && Array.isArray(component.fetch)) {
        await Promise.all(component.fetch.map((f: any) => store.dispatch(f({ viewer }))));
      } else if (component.fetch) {
        await store.dispatch(component.fetch({ viewer }));
      }

      setEditDialog(editDialog);
    },
    [store, viewer]
  );

  const closeDialog = useCallback(
    (message: string, error: boolean) => {
      setEditDialog(null);

      if (message) {
        notify(message, error ? 'error' : 'success');
      }
    },
    [notify]
  );

  const selectProfile = (profile: any, selected: any) => {
    if (selected) {
      setSelectedProfiles((state) => add(state, profile, (p: any) => p.id === profile.id));
    } else {
      setSelectedProfiles((state) =>
        removeAt(
          state,
          state.findIndex((p) => p.id === profile.id)
        )
      );
    }
  };

  const clearProfileSelection = () => {
    setSelectedProfiles([]);
  };

  const handleSubmit = useCallback(
    async (values: any) => {
      if (!viewer.phone && values.phone) {
        try {
          await setAddress(viewer.profile?.id, 'phone', values.phone, true);
        } catch (err) {
          if (hasErrorCode(err, ERROR_CODES.ADDRESS_ADDRESS_TAKEN)) {
            // Returned, not passed to final-form's callback: an async onSubmit is
            // completed again with whatever its promise resolves to.
            return { phone: 'Already in use, please use another' };
          }
          // The request still saves below; the phone is the only thing lost.
          const message = apiErrorMessage(err, PHONE_SAVE_ERROR);
          if (message === PHONE_SAVE_ERROR) Sentry.captureException(err);
          notify(message, 'error');
        }
      }

      // Unreachable in practice: the form this saves renders below the loaded-request
      // guard. Narrowing here rather than asserting keeps the two dereferences below
      // honest if that ever stops being true (ENG-5721).
      if (!expertRequest) return;

      const permissions = expertRequest.permissions || [];
      const canEditAdminFields = permissions.includes('update_admin_fields');
      const canEditQueries = permissions.includes('update_queries');

      try {
        await saveExpertRequest(values, {
          includeAdminFields: canEditAdminFields,
          includeQueries: canEditQueries,
          includeAttachments: false,
        });

        const newAttachments = (values?.attachments || []).filter(
          (attachment: any) => !attachment.id
        );

        const removedAttachments = (expertRequest?.attachments || [])
          .filter(
            (oldAttachment) =>
              !(values?.attachments || []).some(
                (newAttachment: any) => newAttachment.id === oldAttachment.id
              )
          )
          .map((oldAttachment) => oldAttachment.id);

        if (newAttachments.length) {
          await addAttachments({
            variables: {
              expertRequestId: expertRequest.id,
              attachments: newAttachments,
            },
          });
        }
        if (removedAttachments.length) {
          await removeAttachments({
            variables: {
              expertRequestId: expertRequest.id,
              attachmentIds: removedAttachments,
            },
          });
        }
      } catch (err) {
        notify(
          apiErrorMessage(err, 'An error occurred when updating the expert request.'),
          'error'
        );
        return;
      }
      notify('Saved successfully.', 'success');
    },
    [
      viewer,
      saveExpertRequest,
      setAddress,
      notify,
      expertRequest,
      addAttachments,
      removeAttachments,
    ]
  );

  // Hoisted above the guard below so the call stays unconditional. `useFragment` is
  // codegen's identity unmask (`__generated__/fragment-masking.ts` returns its argument),
  // not a React hook — but eslint's rules-of-hooks treats any `use*` as one, and keeping it
  // here is cheaper than an eslint-disable.
  const project = useFragment(PROJECT_FIELDS, expertRequest?.project);

  // ENG-5787: the render below swaps the project bar for a fallback rather than crashing on
  // it, but that state is a defect and not the empty case it looks like — every ER in
  // WEBAPP-CDV / CDW / CDK has a live, readable project. `expertRequest` is undefined until
  // the request is loaded, so a node the context switch emptied is handled as a missing node
  // (see below) rather than misfiling as a missing project.
  useReportMissingProject('expert_request_page', expertRequest);

  // ENG-5721: `UI__SET_USER_CONTEXT` resets every `expertRequests` collection to
  // `initialState`, and a staff context switch dispatches it without awaiting — so the wipe
  // can land *after* this route's loader has already run, leaving the page with no request
  // and nothing on its way to restore one. A navigation still in flight will repopulate the
  // node by itself; when nothing is in flight, re-run the loader here.
  //
  // Capped at a single attempt. If the refetch returns and the node is still absent the
  // request is genuinely unreadable, and retrying would spin forever — WEBAPP-CDJ replaced
  // the page with an error screen, and an endless spinner would not be an improvement.
  const revalidator = useRevalidator();
  const navigation = useNavigation();
  const recoveryAttempt = useRef<Promise<void> | null>(null);
  const [recoveryExhausted, setRecoveryExhausted] = useState(false);

  useEffect(() => {
    if (expertRequest) {
      // Recovered, or never lost — re-arm for any later wipe in the same mount. Dropping the
      // attempt also disowns one still settling, so it cannot end a wipe it did not start.
      recoveryAttempt.current = null;
      if (recoveryExhausted) setRecoveryExhausted(false);
      return;
    }
    // A navigation already in flight re-runs the loader by itself; only step in when
    // nothing else will. The ref keeps this to a single attempt.
    if (recoveryAttempt.current || navigation.state !== 'idle') return;
    // Settlement, not `revalidator.state`, marks the attempt spent: reading the state would
    // flash the terminal message in the render between starting and the router flipping to
    // 'loading'. No cleanup on purpose — cancelling on a re-render would strand the page on
    // a spinner forever.
    const attempt = revalidator.revalidate();
    recoveryAttempt.current = attempt;
    attempt.finally(() => {
      if (recoveryAttempt.current === attempt) setRecoveryExhausted(true);
    });
  }, [expertRequest, navigation.state, recoveryExhausted, revalidator]);

  if (!expertRequest) {
    // Same root as the loaded render below, so React keeps the layout mounted across the swap
    // instead of remounting it, and the focus a context switch leaves on the header survives.
    // One status region spans both states so the terminal message is announced (WCAG 4.1.3).
    return (
      <MediaQuery maxWidth={SCREEN_SM}>
        {() => (
          <LayoutPage showNav selected="expert_requests">
            <div role="status">
              {recoveryExhausted ? (
                <EmptyMessage
                  title="This expert request could not be loaded"
                  body={<Link to="/expert_requests">Back to expert requests</Link>}
                />
              ) : (
                <CircularProgress aria-label="Loading expert request" />
              )}
            </div>
          </LayoutPage>
        )}
      </MediaQuery>
    );
  }

  const { name } = expertRequest;
  const consultations = expertRequest.consultations || [];
  const openConsultationCount = countOpenConsultations(consultations);
  const selectedSection = stateSection;
  const permissions = expertRequest.permissions || [];
  const canEdit = permissions.includes('update');
  const canEditQueries = permissions.includes('update_queries');
  const canAddAttachment = permissions.includes('add_attachment');
  const canRemoveAttachment = permissions.includes('remove_attachment');
  // AC#9: the Save Request panel (Save button + preferred-contact Phone) must
  // follow the §5.5 read-only matrix, not just the coarse `update` permission —
  // otherwise it stays interactive on a Closed ER. Phone is an ER content field,
  // and content-locked ⟺ all-groups-locked under the matrix, so the content gate
  // also governs Save.
  const contentEditable = canEdit && canEditGroup(expertRequest.field_permissions, 'content');

  const projectMembers = project?.members || [];

  const isResearchTeam = projectMembers.some(
    (m) => m.user && m.user.id === viewer.id && (m.role === 'manager' || m.role === 'associate')
  );
  const isClient = projectMembers.some(
    (m) => m.user && m.user.id === viewer.id && (m.role === 'owner' || m.role === 'member')
  );
  const hasAllPermissions = viewer.admin || isResearchTeam;

  const membersToShow = projectMembers
    .filter((m) => m.state === ProjectMemberState.Active)
    .sort((a, b) => {
      const roles = [
        ProjectRole.Manager,
        ProjectRole.Associate,
        ProjectRole.Owner,
        ProjectRole.Member,
        ProjectRole.Viewer,
      ];
      return roles.indexOf(a.role!) - roles.indexOf(b.role!);
    });

  const memberRequests = sortMembers(
    projectMembers.filter((m) => m.state === ProjectMemberState.AwaitingApproval)
  );

  const suggestionsTabAvailable =
    hasAllPermissions || (suggestedCandidates && suggestedCandidates.length > 0);

  const section2 =
    selectedSection === 'suggestions' && !suggestionsTabAvailable ? 'experts' : selectedSection;

  const selectedStatesList =
    section2 === 'suggestions' ? selectedStates.suggestions : selectedStates.experts;
  let candidates = section2 === 'suggestions' ? suggestedCandidates : matchedCandidates;
  if (selectedStatesList.length > 0) {
    candidates = candidates?.filter((c) =>
      // @ts-expect-error TS(2531) FIXME: Object is possibly 'null'.
      selectedStatesList.includes(c.state)
    );
  }
  candidates = candidates || [];

  const candidatePageInfo =
    section2 === 'suggestions' ? suggestedCandidatesPageInfo : matchedCandidatesPageInfo;

  const fetchMoreCandidatesInternal = () => {
    return fetchMoreCandidates(
      section2 === 'suggestions' ? 'suggested' : 'matched',
      candidatePageInfo
    );
  };

  return (
    <MediaQuery maxWidth={SCREEN_SM}>
      {(isMobileVersion: never) => {
        return (
          <LayoutPage showNav selected="expert_requests">
            <Helmet>
              <title>{name}</title>
            </Helmet>
            <ConsultationsStarting />

            {/* An ER is not guaranteed to reach render with a project — the schema makes
                it nullable and the Redux node can carry the request without it, which is
                why every other read below narrows first. ProjectBar dereferences the
                project on its first line, so the same narrowing belongs here
                (Sentry WEBAPP-CDV / CDW / CDK). Reported above rather than silently
                hidden, and replaced rather than dropped, because the bar is this page's
                only way back. */}
            {project ? (
              <ProjectBar
                manageMembers
                viewer={viewer}
                project={project}
                members={membersToShow}
                className="mr-24"
              />
            ) : (
              <ProjectBarFallback to="/expert_requests" className="mr-24">
                Expert Requests
              </ProjectBarFallback>
            )}

            <div className={s.header}>
              <div className="flex min-w-0 flex-1 items-baseline">
                <h3 className={s.name}>{name}</h3>
                {canEdit && (
                  <EditIcon
                    onClick={() => openDialog('name')}
                    aria-label="Edit title"
                    style={{ marginLeft: 10 }}
                    className="shrink-0 translate-y-[2px]"
                  />
                )}
              </div>
              <ErStageDropdown
                stage={expertRequest.stage}
                state={expertRequest.state}
                onSelect={transitions.openTransition}
                allowedActions={permissions}
                disabled={transitions.openModal !== null || transitions.submitting}
                className="ml-16 mr-24 shrink-0"
              />
            </div>

            {project && <MemberRequests projectId={project.id} memberRequests={memberRequests} />}

            <div className={s.content}>
              <div className={s.section}>
                <Tabs
                  value={section2}
                  onChange={(_e, section) => setSection(section)}
                  variant="scrollable"
                >
                  <Tab
                    value="experts"
                    label="Matched Experts"
                    id="expertRequestsMatchedExpertsTab"
                  />
                  {suggestionsTabAvailable && <Tab value="suggestions" label="Suggested Experts" />}
                  <Tab value="consultations" label="Consultations" />
                  <Tab
                    id="expertRequestsAdminRequestDetails"
                    value="details"
                    label="Request Details"
                  />
                  <Tab id="expertRequestsActivityTab" value="activity" label="Activity" />
                </Tabs>

                <ErStageBanner
                  stage={expertRequest.stage}
                  state={expertRequest.state}
                  holdReason={expertRequest.hold_reason}
                  holdResumeDate={expertRequest.hold_resume_date}
                  onSubmit={
                    permissions.includes('submit_for_sourcing')
                      ? () =>
                          transitions.openTransition(
                            ExpertRequestTransitionAction.SubmitForSourcing
                          )
                      : undefined
                  }
                  onResume={
                    permissions.includes('resume')
                      ? () => transitions.openTransition(ExpertRequestTransitionAction.Resume)
                      : undefined
                  }
                  className="mt-16"
                />

                <div className={s.sectionContent}>
                  <div className={s.sectionItems}>
                    {['suggestions', 'experts'].includes(section2) && (
                      <div>
                        {!selectedStates[section2 as 'suggestions' | 'experts'].length &&
                          (viewer.admin || isResearchTeam) &&
                          section2 === 'suggestions' && (
                            <RMStartPromo expertRequest={expertRequest} />
                          )}
                        {candidates.map((c) => (
                          <CandidateWidgetHorizontal
                            key={c.id}
                            showKeywords
                            candidate={c}
                            expertRequest={expertRequest}
                            viewer={viewer}
                            canConfirmMatch={isClient}
                            style={{ padding: 15, marginBottom: 16 }}
                            onRemove={
                              hasAllPermissions
                                ? () => removeExpertRequestCandidate(expertRequest.id, c)
                                : undefined
                            }
                            onSelect={selectProfile}
                            selected={selectedProfiles.includes(c.profile)}
                            isGoodMatch={
                              isGoodMatch && candidateId === c.id ? isGoodMatch : undefined
                            }
                            // @ts-expect-error TS(2531) FIXME: Object is possibly 'null'.
                            ref={candidateRefs[c.id]}
                          />
                        ))}
                        {loadingCandidates && (
                          <div className={s.loading}>
                            <CircularProgress />
                          </div>
                        )}
                        {!loadingCandidates && candidatePageInfo?.hasNextPage && (
                          <Waypoint onEnter={fetchMoreCandidatesInternal} />
                        )}
                        {loadedCandidates && !candidates.length && project && (
                          <EmptyMessage
                            style={{ padding: 16, backgroundColor: surfaceCard }}
                            titleStyle={{ fontSize: 24 }}
                            bodyStyle={{ fontSize: 16 }}
                            border={false}
                            title="Request another expert for this project"
                            body="Looking for another type of expertise for the same project?"
                            action={
                              <div>
                                <Link to={`/request_expert?project_id=${project.id}`}>
                                  <MaterialButton color="secondary">Add New Request</MaterialButton>
                                </Link>
                                {/* ENG-5670: see ExpertRequestPreview - Make a Copy skips
                                    the type picker, so it needs the same account check,
                                    keyed on the ER's own project group like the api's.

                                    Reads `expertRequest.project`, not the unmasked
                                    `project` above: ENG-5715 selects
                                    `group { engagements_enabled }` at the query rather
                                    than inside ProjectFields, because that fragment is
                                    also spread into the two ER save mutations and a
                                    permission-checked non-null field has no business in
                                    a mutation result. `useFragment` is a type-level
                                    unmask, so it cannot see a selection the fragment
                                    does not declare. */}
                                {!isDeprecatedType(expertRequest.er_type) &&
                                  !(
                                    expertRequest.er_type === ExpertRequestType.ConsultingProject &&
                                    isEngagementsEnabledForGroup(expertRequest.project?.group)
                                  ) && (
                                    <Link
                                      to={`/request_expert/details?copy_from=${expertRequest.id}`}
                                    >
                                      <MaterialButton color="secondary">Make a Copy</MaterialButton>
                                    </Link>
                                  )}
                              </div>
                            }
                          />
                        )}
                        <SelectedProfilesBar
                          onRemove={(candidate) => selectProfile(candidate, false)}
                          onClear={() => clearProfileSelection()}
                          profiles={selectedProfiles}
                        >
                          {viewer.admin && (
                            <div>
                              <Button
                                variant="contained"
                                color="teal"
                                size="medium"
                                startIcon={
                                  <FAIcon iconSet="fal" icon="comment-alt-lines" size={20} />
                                }
                                onClick={() => setSendMessageDialogOpen(true)}
                                style={{
                                  marginRight: 10,
                                  textTransform: 'initial',
                                }}
                              >
                                Send Message
                              </Button>
                              <SendMessageDialog
                                open={sendMessageDialogOpen}
                                onClose={() => setSendMessageDialogOpen(false)}
                                onSend={clearProfileSelection}
                                profiles={selectedProfiles}
                                expertRequestId={expertRequest.id}
                              />
                            </div>
                          )}
                        </SelectedProfilesBar>
                      </div>
                    )}

                    {section2 === 'consultations' && (
                      <div>
                        <ConsultationList
                          title="Upcoming"
                          consultations={consultations
                            .filter((c: any) => c.state === 'confirmed')
                            .sort((a: any, b: any) => sortDates(a.starts_at, b.starts_at))}
                        />

                        <ConsultationList
                          title="Awaiting"
                          consultations={consultations
                            .filter((c: any) =>
                              [
                                'negotiating_client_time',
                                'negotiating_expert_time',
                                'awaiting_expert_review',
                              ].includes(c.state)
                            )
                            .sort((a: any, b: any) => sortDates(b.created_at, a.created_at))}
                        />

                        <ConsultationList
                          grid
                          title="Completed"
                          consultations={consultations
                            .filter((c: any) => c.state === 'completed')
                            .sort((a: any, b: any) => sortDates(b.ended_at, a.ended_at))}
                        />

                        <ConsultationList
                          grid
                          title="Canceled"
                          consultations={consultations
                            .filter((c: any) =>
                              ['canceled', 'denied', 'expired', 'incomplete'].includes(c.state)
                            )
                            .sort((a: any, b: any) => sortDates(b.canceled_at, a.canceled_at))}
                        />
                      </div>
                    )}
                    {section2 === 'details' && (
                      <Form
                        onSubmit={handleSubmit}
                        initialValues={expertRequest}
                        // ENG-5453: a save returns fresh permission array refs that trip
                        // react-final-form's reinitialize; keep the user's in-session edits
                        // instead of resetting them to the (now stale) initialValues.
                        keepDirtyOnReinitialize
                        mutators={{
                          ...arrayMutators,
                        }}
                      >
                        {({ handleSubmit, form, values }) => (
                          <Grid container spacing={4} justifyContent="center">
                            <Grid item md={8} sm={12}>
                              <Details
                                isViewerExpert={false}
                                showFields={DETAILS_SHOW_FIELDS}
                                canEdit={canEdit}
                                canEditQueries={canEditQueries}
                                canAddAttachment={canAddAttachment}
                                canRemoveAttachment={canRemoveAttachment}
                                fieldPermissions={expertRequest.field_permissions}
                                change={form.change}
                                values={values}
                                isMobileVersion={isMobileVersion}
                              />
                            </Grid>
                            <Grid
                              item
                              md={4}
                              sm={12}
                              style={{
                                position: 'sticky',
                                top: 0,
                                height: '100%',
                              }}
                            >
                              <SubmitContainer
                                disabled={!contentEditable}
                                handleSubmit={handleSubmit}
                              />
                              <InternalErFields values={values} change={form.change} />
                            </Grid>
                          </Grid>
                        )}
                      </Form>
                    )}
                    {section2 === 'activity' && (
                      <ActivityFeed
                        expertRequestId={expertRequest.id}
                        canSeeInternal={hasAllPermissions}
                      />
                    )}
                  </div>
                  {!['details', 'consultations', 'activity'].includes(section2) && (
                    <div className={s.expertRequestFilters}>
                      <CountBoxes
                        isClient={!viewer.admin && !isResearchTeam}
                        section={section2}
                        expertCounts={candidatePageInfo?.metaData}
                        selectedBoxes={selectedBoxes}
                        onClick={(states: any, boxes: any) => {
                          setSelectedStates(states);
                          setSelectedBoxes(boxes);
                        }}
                      />
                      {isClient && <ReferFriend expertRequestId={expertRequest.id} />}
                    </div>
                  )}
                </div>
              </div>
              <ErTransitionModals
                openModal={transitions.openModal}
                submitting={transitions.submitting}
                error={transitions.error}
                openConsultationCount={openConsultationCount}
                onDismiss={transitions.dismiss}
                onSubmitForSourcing={transitions.submitForSourcing}
                onResume={transitions.resume}
                onReopen={transitions.reopen}
                onPutOnHold={transitions.putOnHold}
                onClose={transitions.closeRequest}
              />
            </div>

            {editDialog && (
              <EditExpertRequest
                open
                onClose={closeDialog}
                initialValues={{
                  ...expertRequest,
                  project_id: project?.id,
                }}
                {...dialogs[editDialog]}
              />
            )}
          </LayoutPage>
        );
      }}
    </MediaQuery>
  );
};

export default connector(ExpertRequestPrivate);
